import React, { useMemo } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import { useRuleContentRefs } from '@pathways/hooks/useRuleContentRefs';
import './styles.less';

function CareTeamPermissionsCard({ loading, pathway }) {
  const { t } = useTranslation();
  const allRules = useMemo(() => {
    if (!pathway) return [];
    const stagedRules = pathway.stages?.flatMap(stage => stage.rules || []) || [];
    const indexEventRules = pathway.indexEvents?.flatMap(ie => ie.rules || []) || [];
    return [...stagedRules, ...indexEventRules];
  }, [pathway]);
  const { rules: hydratedRules } = useRuleContentRefs(allRules);

  const allAvailableContent = useMemo(() => {
    const contentMap = new Map();
    hydratedRules.forEach(rule => {
      if (rule.whatDetail) {
        const contentRef = rule.whatDetail.content;
        if (contentRef && !contentMap.has(contentRef)) {
          contentMap.set(contentRef, {
            ...rule.whatDetail,
            contentRef,
          });
        }
      }
    });
    return Array.from(contentMap.values());
  }, [hydratedRules]);

  const availableContentRefs = useMemo(
    () => new Set(allAvailableContent.map(c => c.contentRef).filter(Boolean)),
    [allAvailableContent],
  );

  const callToActionMap = useMemo(() => {
    const map = new Map();
    const ctaList = pathway?.clientConfiguration?.callToAction || [];
    ctaList.forEach(cta => map.set(cta.id, cta));
    return map;
  }, [pathway]);

  const roles = useMemo(() => {
    if (!pathway) return [];

    const contentRefMap = new Map();
    allAvailableContent.forEach(content => {
      if (content.contentRef) {
        contentRefMap.set(content.contentRef, content);
      }
    });

    const hydrateRoles = rolesToHydrate =>
      rolesToHydrate.map(role => ({
        ...role,
        permissions: role.permissions.map(permission => {
          if (permission.type === 'cta') {
            return {
              ...permission,
              callToAction: callToActionMap.get(permission.id) || permission.callToAction,
            };
          }

          if (permission.type === 'rule' || (!permission.type && permission.contentRef)) {
            return {
              ...permission,
              form: contentRefMap.get(permission.contentRef) || permission.form,
            };
          }

          return permission;
        }),
      }));

    return hydrateRoles(pathway.careTeamPermissions || pathway.roles || []);
  }, [pathway, callToActionMap, allAvailableContent]);

  const isCTA = record => record.type === 'cta' || (!record.type && record.callToAction);
  const isForm = record => record.type === 'rule' || (!record.type && record.form);

  if (!pathway || !roles || roles.length === 0) return null;

  return (
    <Card.Half
      className="role-rules-card"
      loading={loading}
      title={t('pathways:ProcedurePathways.wizard.review.careTeamRules.header')}
    >
      <Tabs>
        {roles.map((role, index) => {
          const dataSource = role.permissions.map(permission => ({
            ...permission,
            key: permission.id ?? permission.form?.content ?? permission.form?.contentRef,
          }));

          return (
            <Tabs.TabPane tab={role.name} key={`role-${index}`}>
              {role.description && <p className="role-description">{role.description}</p>}
              <ul className="role-forms-list">
                {dataSource.map(record => {
                  let itemName = null;
                  if (isCTA(record)) {
                    itemName = record.callToAction?.name;
                  } else if (
                    isForm(record) &&
                    record.contentRef &&
                    !availableContentRefs.has(record.contentRef)
                  ) {
                    itemName = (
                      <span className="rule-status-text">
                        {t('pathways:ProcedurePathways.wizard.careTeamRules.formRemoved')}
                      </span>
                    );
                  } else {
                    itemName =
                      record.form?.name || record.form?.title || record.form?.latestVersion?.name;
                  }

                  return <li key={record.key}>{itemName}</li>;
                })}
              </ul>
            </Tabs.TabPane>
          );
        })}
      </Tabs>
    </Card.Half>
  );
}

export default CareTeamPermissionsCard;
